import {
  STUDIO_WORKSPACE_GROUP_PROPERTY,
  STUDIO_WORKSPACE_GROUP_PROPERTY_VALUE,
} from 'src/constants/studioFtux';

/**
 * Workspace group properties for PostHog client `group()` / SSR `getAllFlags`.
 * Studio portals include the cohort trait so flags targeting `studio="true"`
 * evaluate before (or without relying on) async `$groupidentify` ingest.
 *
 * The trait derives from persisted PortalConfig, so every dashboard load
 * re-asserts it: a `$groupidentify` the backend failed to deliver at workspace
 * creation is repaired here rather than leaving the workspace untargetable.
 */
export const getWorkspacePosthogGroupProperties = (
  portalName: string,
  isStudioUser: boolean,
): Record<string, string> => ({
  name: portalName,
  ...getStudioGroupProperties(isStudioUser),
});

/** Options bag for posthog-node `getAllFlags` workspace evaluation. */
export const getPosthogGetAllFlagsOptions = (
  portalHeader: string,
  isStudioUser: boolean,
): {
  groups: { workspace: string };
  groupProperties?: {
    workspace: Record<string, string>;
  };
} => {
  const studioProperties = getStudioGroupProperties(isStudioUser);
  return {
    groups: { workspace: portalHeader },
    ...(Object.keys(studioProperties).length > 0
      ? { groupProperties: { workspace: studioProperties } }
      : {}),
  };
};

/**
 * Cache key so Studio and Classic evaluations never share a cached result — the
 * two resolve different flags, and only the Studio cohort carries group
 * properties.
 */
export const getPosthogFlagsCacheKey = (
  portalHeader: string,
  isStudioUser: boolean,
): string => (isStudioUser ? `${portalHeader}:studio` : portalHeader);

const getStudioGroupProperties = (
  isStudioUser: boolean,
): Record<string, string> =>
  isStudioUser
    ? {
        [STUDIO_WORKSPACE_GROUP_PROPERTY]:
          STUDIO_WORKSPACE_GROUP_PROPERTY_VALUE,
      }
    : {};
